type MobileTab = "products" | "themenwelten";

const initEkMegaMenuMobile = (scope: ParentNode = document) => {
  const megaMenus = Array.from(scope.querySelectorAll("[data-mega-menu-mobile]")) as HTMLElement[];

  for (const megaMenu of megaMenus) {
    if (megaMenu.dataset.initialized === "true") continue;
    megaMenu.dataset.initialized = "true";

    // Overlay open/close elements
    const overlay = megaMenu.closest("[data-mega-menu-mobile-overlay]") as HTMLElement | null;

    // Get holder BEFORE moving overlay out of the DOM tree
    const holder = megaMenu.closest(".ek-megamenuHolder") as HTMLElement | null;

    // Move overlay to document.body so it escapes the header's transform
    // (transform on a parent creates a containing block that breaks fixed positioning)
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }

    const drawer = overlay?.querySelector("[data-mega-menu-mobile-drawer]") as HTMLElement | null;
    const openButton = document.getElementById("mobile-mega-menu") as HTMLButtonElement | null;
    const closeButton = megaMenu.querySelector("#close-mobile-mega-menu") as HTMLButtonElement | null;
    const backdrop = overlay?.querySelector("[data-mega-menu-mobile-backdrop]") as HTMLElement | null;

    // Clean up previous global listeners on re-init (theme editor section reload)
    if ((megaMenu as any).__ekMobileAbort) {
      (megaMenu as any).__ekMobileAbort.abort();
    }
    const controller = new AbortController();
    (megaMenu as any).__ekMobileAbort = controller;

    const openMenu = () => {
      if (!overlay || !drawer || !backdrop) return;
      // Enable interaction on overlay
      overlay.classList.remove("pointer-events-none");
      // Slide panel in
      drawer.classList.remove("-translate-x-full");
      drawer.classList.add("translate-x-0");
      // Fade backdrop in
      backdrop.classList.remove("opacity-0", "pointer-events-none");
      backdrop.classList.add("opacity-100", "pointer-events-auto");
      // Lock body scroll
      document.body.style.overflow = "hidden";
      // Update ARIA state
      if (openButton) openButton.setAttribute("aria-expanded", "true");
      // Move focus into the drawer
      if (closeButton) closeButton.focus();
      // Ensure solid background
      if (holder) holder.classList.add("bg-white");
    };

    let resetMenu: (() => void) | null = null;

    const closeMenu = () => {
      if (!overlay || !drawer || !backdrop) return;
      // Slide panel out
      drawer.classList.remove("translate-x-0");
      drawer.classList.add("-translate-x-full");
      // Fade backdrop out
      backdrop.classList.remove("opacity-100", "pointer-events-auto");
      backdrop.classList.add("opacity-0", "pointer-events-none");
      // Restore body scroll
      document.body.style.overflow = "";
      // Update ARIA state
      if (openButton) openButton.setAttribute("aria-expanded", "false");
      // Return focus to the trigger button
      if (openButton) openButton.focus();
      // Remove solid background if at top
      if (holder && window.scrollY <= 0) holder.classList.remove("bg-white");
      // Reset to products root + scroll to top after slide-out finishes
      drawer.addEventListener(
        "transitionend",
        () => {
          overlay.classList.add("pointer-events-none");
          if (resetMenu) resetMenu();
          drawer.scrollTop = 0;
        },
        { once: true }
      );
    };

    // Escape key closes the menu
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !overlay?.classList.contains("pointer-events-none")) {
        closeMenu();
      }
    }, { signal: controller.signal });

    // Focus trap — keep Tab cycling inside the drawer when open
    overlay?.addEventListener("keydown", (e) => {
      if (e.key !== "Tab" || overlay.classList.contains("pointer-events-none")) return;

      const focusableEls = drawer?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableEls || focusableEls.length === 0) return;

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }, { signal: controller.signal });

    if (openButton) openButton.addEventListener("click", openMenu, { signal: controller.signal });
    if (closeButton) closeButton.addEventListener("click", closeMenu, { signal: controller.signal });
    if (backdrop) backdrop.addEventListener("click", closeMenu, { signal: controller.signal });

    // Navigation links use native <a> behaviour — do not close the drawer here.
    // Synchronous closeMenu() on click can cancel navigation (especially on iOS).

    const tabsNav = megaMenu.querySelector("[data-mega-menu-mobile-tabs]") as HTMLElement | null;
    const indicator = megaMenu.querySelector("[data-mega-menu-mobile-indicator]") as HTMLElement | null;
    const backButton = megaMenu.querySelector("[data-mega-menu-mobile-back]") as HTMLButtonElement | null;
    const triggers = Array.from(megaMenu.querySelectorAll("[data-mega-menu-mobile-tab]")) as HTMLButtonElement[];
    const views = Array.from(megaMenu.querySelectorAll("[data-mega-menu-mobile-view]")) as HTMLElement[];
    const productsRoot = megaMenu.querySelector("[data-mega-menu-mobile-products-root]") as HTMLElement | null;
    const submenuTriggers = Array.from(megaMenu.querySelectorAll("[data-mega-menu-mobile-open-submenu]")) as HTMLElement[];
    const submenuViews = Array.from(megaMenu.querySelectorAll("[data-mega-menu-mobile-submenu]")) as HTMLElement[];

    if (!backButton || views.length === 0) continue;
    const hasTabs = tabsNav && indicator && triggers.length > 0;

    let activeTab: MobileTab = "products";
    let activeSubmenu: string | null = null;

    const getTrigger = (tab: MobileTab) =>
      triggers.find((trigger) => trigger.dataset.megaMenuMobileTab === tab) || null;

    const getSubmenuPanel = (submenuId: string | null) =>
      submenuViews.find((panel) => panel.dataset.megaMenuMobileSubmenu === submenuId) || null;

    const moveIndicator = (trigger: HTMLElement | null) => {
      if (!hasTabs || !indicator || !tabsNav) return;
      if (!trigger) {
        indicator.style.width = "0px";
        indicator.style.opacity = "0";
        indicator.style.transform = "translateX(0px)";
        return;
      }

      const navRect = tabsNav.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const left = triggerRect.left - navRect.left;

      indicator.style.width = `${triggerRect.width}px`;
      indicator.style.opacity = "1";
      indicator.style.transform = `translateX(${left}px)`;
    };

    const setTriggerState = (trigger: HTMLButtonElement, isActive: boolean) => {
      trigger.ariaSelected = isActive ? "true" : "false";
      trigger.classList.toggle("font-semibold", isActive);
      trigger.classList.toggle("font-normal", !isActive);
      trigger.classList.toggle("text-secondaryhighlight", !isActive);
    };

    const updateTriggers = () => {
      for (const trigger of triggers) {
        setTriggerState(trigger, trigger.dataset.megaMenuMobileTab === activeTab);
      }
    };

    // --- Horizontal slide transitions for products root ↔ submenu ---

    const animatePanel = (panel: HTMLElement, animation: string) => {
      panel.style.animation = "none";
      // Force reflow so the animation restarts cleanly
      void panel.offsetWidth;
      panel.style.animation = animation;
    };

    const updateSubmenuAriaExpanded = (openSubmenuId: string | null) => {
      for (const trigger of submenuTriggers) {
        const isOpen = trigger.dataset.megaMenuMobileOpenSubmenu === openSubmenuId;
        trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
      }
    };

    const showSubmenu = (submenuPanel: HTMLElement) => {
      if (!productsRoot) return;
      if (drawer) drawer.scrollTop = 0;
      productsRoot.hidden = true;
      for (const view of submenuViews) {
        view.hidden = view !== submenuPanel;
      }
      updateSubmenuAriaExpanded(submenuPanel.dataset.megaMenuMobileSubmenu || null);
      animatePanel(submenuPanel, "megaMenuSlideInFromRight 250ms cubic-bezier(0.2, 0, 0, 1) both");
    };

    const showRoot = () => {
      if (!productsRoot) return;
      for (const view of submenuViews) {
        view.hidden = true;
      }
      productsRoot.hidden = false;
      updateSubmenuAriaExpanded(null);
      animatePanel(productsRoot, "megaMenuSlideInFromLeft 200ms cubic-bezier(0.2, 0, 0, 1) both");
    };

    const resetToRoot = () => {
      if (!productsRoot) return;
      for (const view of submenuViews) {
        view.hidden = true;
      }
      productsRoot.hidden = false;
      productsRoot.style.animation = "";
      updateSubmenuAriaExpanded(null);
    };

    const updateViews = () => {
      for (const view of views) {
        const isActiveView = view.dataset.megaMenuMobileView === activeTab;
        view.hidden = !isActiveView;
      }

      if (activeTab !== "products") {
        resetToRoot();
        return;
      }

      if (!activeSubmenu) {
        showRoot();
        return;
      }

      const submenuPanel = getSubmenuPanel(activeSubmenu);
      if (submenuPanel) {
        showSubmenu(submenuPanel);
      }
    };

    const updateBackButton = () => {
      backButton.hidden = activeTab === "products" && activeSubmenu === null;
    };

    const render = () => {
      updateTriggers();
      updateViews();
      updateBackButton();
      moveIndicator(getTrigger(activeTab));
    };

    const showProductsRoot = () => {
      activeTab = "products";
      activeSubmenu = null;
      render();
    };

    resetMenu = showProductsRoot;

    const showTab = (tab: MobileTab) => {
      activeTab = tab;
      activeSubmenu = null;
      render();
    };

    const openSubmenu = (submenuId: string) => {
      activeTab = "products";
      activeSubmenu = submenuId;
      render();
    };

    for (const trigger of triggers) {
      const tab = trigger.dataset.megaMenuMobileTab;
      if (tab !== "products" && tab !== "themenwelten") continue;

      trigger.addEventListener("click", () => {
        showTab(tab);
      });
    }

    for (const trigger of submenuTriggers) {
      const submenuId = trigger.dataset.megaMenuMobileOpenSubmenu;
      if (!submenuId) continue;

      trigger.addEventListener("click", () => {
        openSubmenu(submenuId);
      });
    }

    backButton.addEventListener("click", () => {
      showProductsRoot();
    });

    window.addEventListener("resize", () => {
      moveIndicator(getTrigger(activeTab));
    }, { signal: controller.signal });

    render();
  }
};

/*
 * Lazy panel: normal pages render an empty drawer (see snippets/mega-menu-mobile.liquid). The panel is
 * fetched via /?view=megamenu&section_id=ek-megamenu (templates/index.megamenu.json) on first intent to open (or when idle on mobile),
 * inserted into the drawer and initialised; the original click is then replayed.
 */
const shopifyRoot = (window as Window & { Shopify?: { routes?: { root?: string } } }).Shopify?.routes?.root || "/";
const PANEL_URL = `${shopifyRoot}?view=megamenu&section_id=ek-megamenu`;
let panelRequest: Promise<boolean> | null = null;

const loadMobilePanel = (): Promise<boolean> => {
  if (panelRequest) return panelRequest;

  panelRequest = (async () => {
    const drawer = document.querySelector<HTMLElement>("[data-mega-menu-mobile-drawer]");
    if (!drawer?.querySelector("[data-mega-menu-mobile-placeholder]")) return true;

    try {
      const response = await fetch(PANEL_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      const fetchedDrawer = html.querySelector("[data-mega-menu-mobile-drawer]");
      if (!fetchedDrawer?.querySelector("[data-mega-menu-mobile]")) throw new Error("Panel missing in response");

      drawer.innerHTML = fetchedDrawer.innerHTML;
      initEkMegaMenuMobile(drawer);
      return true;
    } catch (error) {
      console.error("Mobile menu could not be loaded:", error);
      panelRequest = null;
      return false;
    }
  })();

  return panelRequest;
};

const initEkMegaMenuMobileLazy = () => {
  const openButton = document.getElementById("mobile-mega-menu");
  if (!openButton || openButton.dataset.lazyPanelBound === "true") return;
  if (!document.querySelector("[data-mega-menu-mobile-placeholder]")) return;
  openButton.dataset.lazyPanelBound = "true";

  const warmUp = () => void loadMobilePanel();
  for (const type of ["pointerdown", "touchstart", "mouseenter", "focus"]) {
    openButton.addEventListener(type, warmUp, { once: true, passive: true });
  }

  // Until the panel is in place, hold the click and replay it once initEkMegaMenuMobile has bound its handlers
  openButton.addEventListener(
    "click",
    async (event) => {
      if (document.querySelector("[data-mega-menu-mobile]")) return;
      event.stopImmediatePropagation();
      openButton.setAttribute("aria-busy", "true");
      const loaded = await loadMobilePanel();
      openButton.removeAttribute("aria-busy");
      if (loaded) openButton.click();
    },
    { capture: true },
  );

  // The burger menu is the main navigation on phones — have it ready shortly after load
  if (window.matchMedia("(max-width: 1023px)").matches) {
    const whenIdle = () => ("requestIdleCallback" in window ? requestIdleCallback(warmUp) : setTimeout(warmUp, 2000));
    if (document.readyState === "complete") {
      whenIdle();
    } else {
      window.addEventListener("load", whenIdle, { once: true });
    }
  }
};

export { initEkMegaMenuMobile, initEkMegaMenuMobileLazy };
